import * as Sentry from '@sentry/tanstackstart-react';

import { resolveSentryPlatform } from '#features/analytics/sentry/dsn';
import { envConfig } from '#constants/envConfig';

/**
 * Следы звонка, развалившегося так, что ни сервер, ни GlitchTip ничего не видят: собеседник так и
 * не вошёл в комнату, и клиент сам кладёт трубку через 25 секунд. На сервере это обычный hangup.
 *
 * Только dev: на проде такие события - спам. Признак окружения тот же, что выбирает dev-проекты
 * GlitchTip в `resolveSentryDsn`, - иначе события уходили бы туда, куда их не ждут. Считать случаи
 * на проде помогает сводка качества: там та же причина попадает в `media_quality.disconnect_reason`.
 */
export type CallIncident =
  | 'peer_never_joined'
  | 'peer_gone'
  | 'caller_never_active'
  /** iOS: разговор начался, а медиа так и не подключилось (не путать с недошедшим CALL_ACCEPTED). */
  | 'media_never_connected';

export type CallIncidentDetails = {
  callId: string;
  role: 'caller' | 'callee';
  /** Где замечено: таймер пустой комнаты, кадр WS, снимок нативного звонка. */
  stage: string;
  peerEverJoined?: boolean;
  /** От ответа (или начала набора, если ответа клиент так и не увидел) до события. */
  msSinceAnswer?: number | null;
  connectionState?: string | null;
  /** Сколько звонящий провёл в наборе, так и не перейдя в разговор. */
  msInOutgoing?: number | null;
  /** Код конца, как его назвал клиент или нативный слой (`failed`, `ended`), не текст. */
  endReason?: string | null;
};

type BreadcrumbData = Record<string, string | number | boolean | null | undefined>;

/** Миллисекунды от отметки до сейчас; нет отметки - нет и числа. */
export const msSince = (at: number | null | undefined): number | null =>
  at == null ? null : Date.now() - at;

const enabled = (): boolean => envConfig.SENTRY_ENVIRONMENT === 'development';

/** Один случай на пару (звонок, вид) за жизнь страницы: таймер и сверки срабатывают повторно. */
const reported = new Set<string>();

export const reportCallIncident = (incident: CallIncident, details: CallIncidentDetails): void => {
  if (!enabled()) return;
  const key = `${details.callId}:${incident}`;
  if (reported.has(key)) return;
  reported.add(key);

  const platform = resolveSentryPlatform();
  const shell = envConfig.IS_NATIVE ? 'native' : 'web';
  Sentry.withScope((scope) => {
    scope.setTag('scope', 'callRoom');
    scope.setTag('type', 'call-incident');
    scope.setTag('incident', incident);
    scope.setTag('callId', details.callId);
    scope.setTag('role', details.role);
    scope.setTag('platform', platform);
    scope.setTag('shell', shell);
    scope.setTag('stage', details.stage);
    scope.setLevel('warning');
    scope.setContext('callIncident', {
      callId: details.callId,
      role: details.role,
      platform,
      shell,
      stage: details.stage,
      peerEverJoined: details.peerEverJoined ?? null,
      msSinceAnswer: details.msSinceAnswer ?? null,
      connectionState: details.connectionState ?? null,
      msInOutgoing: details.msInOutgoing ?? null,
      endReason: details.endReason ?? null,
    });
    Sentry.captureMessage(`call incident: ${incident}`);
  });
};

/** Вехи звонка для разбора события выше. Без токенов и адресов комнаты. */
export const callBreadcrumb = (message: string, data?: BreadcrumbData): void => {
  if (!enabled()) return;
  Sentry.addBreadcrumb({ category: 'call', level: 'info', message, data });
};
